import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface MetaConfig {
    appId: string;
    configurationId: string;
}

/**
 * Resposta de `ConfigurarWhatsapp/Status`. `limiteConexaoPorPlataforma` e
 * `conexoesPorPlataforma` (chave = PlataformaEnum) chegaram em 30/09/2026 para o
 * menu "Conectar canal" saber o uso do plano antes do clique; uma API antiga
 * não os manda, por isso são opcionais.
 */
export interface StatusWhatsapp {
    /** Vem do plano (limite nulo ou > 1), não mais de uma flag da empresa. */
    multiWhatsappHabilitado: boolean;
    usaAPIMeta?: boolean;
    usaAPIEvolution?: boolean;
    /** Limite do plano por canal; para o WhatsApp vale Meta + Evolution somados. */
    limiteConexaoPorPlataforma?: number | null;
    conexoesPorPlataforma?: Record<string, number>;
    /* Uso do WhatsApp já calculado pelo backend (Meta + Evolution somados),
       para o front não refazer a regra. `limiteWhatsapp` nulo = ilimitado. */
    limiteWhatsapp?: number | null;
    whatsappsConectados?: number;
    podeConectarWhatsapp?: boolean;
    motivoBloqueioWhatsapp?: string | null;
}

export interface MetaConnectionRequest {
    code: string;
    phoneNumberId: string;
    wabaId: string;
}

export const MetaService = {
    /**
     * Recupera as configurações do Meta App do backend (configuração do WhatsApp).
     *
     * Sem fallback chumbado: com três configurações diferentes (WhatsApp, Instagram e
     * tráfego pago), devolver IDs fixos faria a pessoa achar que conectou anúncios
     * quando conectou WhatsApp. Falha é falha.
     */
    getMetaConfig: async (): Promise<{ sucesso: boolean; mensagem?: string; dados?: MetaConfig }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/ConfigurarWhatsapp/MetaConfig`);
            if (response.ok) {
                return await response.json();
            }
            return { sucesso: false, mensagem: 'Configuração da Meta indisponível.' };
        } catch (error) {
            console.error('Erro ao obter configuração da Meta:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    },

    /**
     * Envia os dados de conexão para o backend.
     */
    configurarCoexistencia: async (payload: MetaConnectionRequest): Promise<{ sucesso: boolean; mensagem: string }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/ConfigurarWhatsapp/Coexistencia`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                return await response.json();
            }

            const errorData = await response.json();
            return { sucesso: false, mensagem: errorData.mensagem || 'Erro ao configurar coexistência.' };
        } catch (error) {
            console.error('Erro ao chamar backend:', error);
            // Mock para testes de UI
            return { sucesso: true, mensagem: 'Conectado com sucesso (MOCK)!' };
        }
    },

    /**
     * Verifica qual API o usuário está utilizando.
     */
    checkStatus: async (): Promise<{ sucesso: boolean; usaAPIMeta: boolean; usaAPIEvolution: boolean }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/ConfigurarWhatsapp/Status`);
            if (response.ok) {
                const data = await response.json();
                return {
                    sucesso: true,
                    usaAPIMeta: data.dados?.usaAPIMeta || false,
                    usaAPIEvolution: data.dados?.usaAPIEvolution || false
                };
            }
        } catch {
            console.warn('Backend endpoint /Status não encontrado.');
        }

        return { sucesso: true, usaAPIMeta: false, usaAPIEvolution: false };
    },

    /**
     * Retorna status completo: se multi-conexão está habilitado e a lista de conexões WhatsApp.
     */
    getWhatsappStatus: async (): Promise<{ sucesso: boolean; dados: StatusWhatsapp }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/ConfigurarWhatsapp/Status`);
            if (response.ok) {
                return await response.json();
            }
        } catch {
            console.warn('Backend endpoint /Status não encontrado.');
        }
        return { sucesso: false, dados: { multiWhatsappHabilitado: false } };
    },
};
